import React, { ReactNode, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useAppSelector } from '../hooks';
import { usePdfExportInputs } from '../hooks/usePdfExportInputs';
import type { MeasurementData } from '../store/reducers/workspace.reducer';
import { selectLampWireframeMap } from '../store/selectors/workspace.selector';
import type { Lamp } from '../types/lamp';
import type { Light } from '../types/light';
import {
  buildLuminairePlotSignature,
  buildLuminairePlotSources,
  prefetchLuminaireIesGrids,
  resolveLuminaireIesGrids,
} from '../utils/pdfPlotGeneration/loadLuminaireGrids';
import { pdfPlotWorkerManager } from '../workers/pdfPlotWorkerManager';

/* eslint-disable react-refresh/only-export-components */

interface FrontendReportImagesContextValue {
  images: Record<string, string>;
  isGenerating: boolean;
  /** True after the latest plot run finished (success or failure). */
  generationComplete: boolean;
  useFrontendPlots: boolean;
}

const FrontendReportImagesContext = createContext<FrontendReportImagesContextValue>({
  images: {},
  isGenerating: false,
  generationComplete: false,
  useFrontendPlots: false,
});

function usePlotGeneration(
  measurementData: MeasurementData[] | null,
  enabled: boolean,
  workspaceLamps: Lamp[],
  versionLights: Light[] | undefined,
) {
  const [images, setImages] = useState<Record<string, string>>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationComplete, setGenerationComplete] = useState(false);
  const wireframeMap = useAppSelector(selectLampWireframeMap);
  const wireframeMapRef = useRef(wireframeMap);
  wireframeMapRef.current = wireframeMap;
  const generationIdRef = useRef(0);
  const workspaceLampsRef = useRef(workspaceLamps);
  workspaceLampsRef.current = workspaceLamps;
  const versionLightsRef = useRef(versionLights);
  versionLightsRef.current = versionLights;

  // Photometry + mounting height (2 dp) — height drives iso-illuminance; rounding ignores raycast noise.
  const lampSignature = useMemo(
    () => buildLuminairePlotSignature(workspaceLamps, versionLights),
    [workspaceLamps, versionLights],
  );

  const measurementSignature = useMemo(
    () =>
      (measurementData ?? [])
        .map((m) => m.uuid)
        .sort()
        .join(','),
    [measurementData],
  );

  useEffect(() => {
    if (!enabled || !measurementData || measurementData.length === 0) {
      setImages({});
      setIsGenerating(false);
      setGenerationComplete(false);
      return;
    }

    let cancelWorker: (() => void) | undefined;
    const debounceTimer = setTimeout(() => {
      const generationId = ++generationIdRef.current;
      setIsGenerating(true);
      setGenerationComplete(false);

      const run = async () => {
        const sources = buildLuminairePlotSources(workspaceLampsRef.current, versionLightsRef.current);
        try {
          const getWireframeGrid = (lampUuid: string) => wireframeMapRef.current.get(lampUuid)?.wireframe?.iesGrid;
          const iesGrids = await resolveLuminaireIesGrids(sources, getWireframeGrid);

          if (generationId !== generationIdRef.current) return;

          const { promise, cancel } = pdfPlotWorkerManager.generate({
            measurementData,
            luminaireSources: sources,
            iesGridsByUuid: iesGrids,
          });
          cancelWorker = cancel;

          const generated = await promise;
          if (generationId !== generationIdRef.current) return;
          setImages(generated);
        } catch (error) {
          if (generationId !== generationIdRef.current) return;
          if (!(error instanceof DOMException && error.name === 'AbortError')) {
            console.error('FrontendReportImages: plot generation failed:', error);
          }
          setImages({});
        } finally {
          if (generationId === generationIdRef.current) {
            setIsGenerating(false);
            setGenerationComplete(true);
          }
        }
      };

      void run();
    }, 300);

    return () => {
      clearTimeout(debounceTimer);
      cancelWorker?.();
    };
    // Grids are resolved inside run(); do not restart when wireframe map loading state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, measurementSignature, lampSignature]);

  return { images, isGenerating, generationComplete };
}

export const FrontendReportImagesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { lamps, activeVersion, measurementData, useFrontendPlots, pdfExportEnabled } = usePdfExportInputs();
  const frontendMeasurementCalculating = useAppSelector((s) => s.workspace.frontendMeasurementCalculating);
  const wireframeMap = useAppSelector(selectLampWireframeMap);
  const wireframeMapRef = useRef(wireframeMap);
  wireframeMapRef.current = wireframeMap;

  const { images, isGenerating, generationComplete } = usePlotGeneration(
    measurementData,
    useFrontendPlots,
    lamps,
    activeVersion?.lights,
  );

  // Warm LDC/IES caches while measurement is computing so plot generation can start immediately after.
  useEffect(() => {
    if (!pdfExportEnabled) return;
    if (!frontendMeasurementCalculating && !measurementData) return;

    const sources = buildLuminairePlotSources(lamps, activeVersion?.lights);
    prefetchLuminaireIesGrids(sources, (uuid) => wireframeMapRef.current.get(uuid)?.wireframe?.iesGrid);
  }, [pdfExportEnabled, frontendMeasurementCalculating, measurementData, lamps, activeVersion?.lights]);

  const value = useMemo(
    () => ({ images, isGenerating, generationComplete, useFrontendPlots }),
    [images, isGenerating, generationComplete, useFrontendPlots],
  );

  return <FrontendReportImagesContext.Provider value={value}>{children}</FrontendReportImagesContext.Provider>;
};

export function useFrontendReportImages(): FrontendReportImagesContextValue {
  return useContext(FrontendReportImagesContext);
}
